
import { supabase } from '../integrations/supabase/client';

export interface TargetNote {
  id: string;
  target_code: string;
  session_id: string;
  user_id: string;
  note_type: 'text' | 'image';
  note_content: string;
  submitted_at: string;
}

export const targetNotesService = {
  // Store a note for a target
  storeNote: async (
    targetCode: string,
    sessionId: string,
    noteType: 'text' | 'image',
    noteContent: string
  ): Promise<TargetNote | null> => {
    try {
      console.log('📝 targetNotesService: Storing note:', { targetCode, sessionId, noteType, noteContent: noteContent.substring(0, 100) + '...' });
      
      // Get current authenticated user - this is the key fix
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('❌ targetNotesService: Auth error when storing note:', authError);
        return null;
      }
      
      if (!user) {
        console.error('❌ targetNotesService: No authenticated user for storing note');
        return null;
      }
      
      console.log('📝 targetNotesService: Authenticated user ID:', user.id);
      
      const { data, error } = await supabase
        .from('target_notes')
        .insert({
          target_code: targetCode,
          session_id: sessionId,
          user_id: user.id, // Include user_id for RLS policies
          note_type: noteType,
          note_content: noteContent
        })
        .select()
        .single();

      if (error) {
        console.error('❌ targetNotesService: Database error storing note:', error);
        console.error('❌ targetNotesService: Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return null;
      }

      console.log('✅ targetNotesService: Note stored successfully:', data);
      return data as TargetNote;
    } catch (error) {
      console.error('❌ targetNotesService: Exception storing note:', error);
      return null;
    }
  },

  // Get all notes for a target code
  getNotesByTargetCode: async (targetCode: string): Promise<TargetNote[]> => {
    try {
      console.log('📝 targetNotesService: Getting notes for target code:', targetCode);
      
      // Get current authenticated user for consistency
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('❌ targetNotesService: Auth error when retrieving notes:', authError);
        return [];
      }
      
      if (!user) {
        console.error('❌ targetNotesService: No authenticated user for retrieving notes');
        return [];
      }
      
      console.log('📝 targetNotesService: Authenticated user ID for retrieval:', user.id);
      
      const { data, error } = await supabase
        .from('target_notes')
        .select('*')
        .eq('target_code', targetCode)
        .eq('user_id', user.id) // Explicit user filtering for clarity
        .order('submitted_at', { ascending: true });

      if (error) {
        console.error('❌ targetNotesService: Database error getting notes:', error);
        console.error('❌ targetNotesService: Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          targetCode,
          userId: user.id
        });
        return [];
      }

      console.log('✅ targetNotesService: Raw notes retrieved from database:', data);
      console.log('📝 targetNotesService: Notes count:', data?.length || 0);
      
      // Log each note for debugging
      if (data && data.length > 0) {
        data.forEach((note, index) => {
          console.log(`📝 targetNotesService: Note ${index + 1}:`, {
            id: note.id,
            target_code: note.target_code,
            user_id: note.user_id,
            note_type: note.note_type,
            content_preview: note.note_content.substring(0, 100),
            submitted_at: note.submitted_at
          });
        });
      } else {
        console.log('📝 targetNotesService: No notes found in database for target:', targetCode);
      }
      
      return (data as TargetNote[]) || [];
    } catch (error) {
      console.error('❌ targetNotesService: Exception getting notes:', error);
      return [];
    }
  },

  // Get all notes for a session
  getNotesBySession: async (sessionId: string): Promise<TargetNote[]> => {
    try {
      console.log('📝 targetNotesService: Getting notes for session:', sessionId);
      
      const { data, error } = await supabase
        .from('target_notes')
        .select('*')
        .eq('session_id', sessionId)
        .order('submitted_at', { ascending: true });

      if (error) {
        console.error('❌ targetNotesService: Error getting session notes:', error);
        return [];
      }

      console.log('✅ targetNotesService: Session notes retrieved:', data);
      return (data as TargetNote[]) || [];
    } catch (error) {
      console.error('❌ targetNotesService: Exception getting session notes:', error);
      return [];
    }
  },

  // Format notes for AI analysis - enhanced formatting for better analysis
  formatNotesForAnalysis: (notes: TargetNote[]): string => {
    if (notes.length === 0) return '';

    const textNotes = notes.filter(note => note.note_type === 'text');
    const imageNotes = notes.filter(note => note.note_type === 'image');

    let formattedContent = '';

    if (textNotes.length > 0) {
      formattedContent += `USER'S TEXT IMPRESSIONS:\n`;
      textNotes.forEach((note, index) => {
        const timestamp = new Date(note.submitted_at).toLocaleTimeString();
        formattedContent += `${index + 1}. [${timestamp}] "${note.note_content.trim()}"\n`;
      });
    }

    if (imageNotes.length > 0) {
      if (formattedContent) formattedContent += '\n';
      formattedContent += `USER'S UPLOADED IMAGES (${imageNotes.length} image${imageNotes.length > 1 ? 's' : ''}):\n`;
      
      // Include the base64 image data directly in the message content
      imageNotes.forEach((note, index) => {
        const timestamp = new Date(note.submitted_at).toLocaleTimeString();
        formattedContent += `Image ${index + 1} [${timestamp}]: ${note.note_content}\n`;
      });
    }

    return formattedContent;
  }
};
